import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import { useAppDispatch, useAppSelector } from 'hooks/store';
import { useSignalRCallScribeEvents } from 'hooks/useSignalRCallScribeEvents';
import { Integrations } from 'constants/integrations';
import { setConfigPsa, ConfigurationSelector } from 'store/configuration';
import {
  CallScribeAnsweredAtForCallSelector,
  CallScribeParticipantsForCallSelector,
} from 'store/callScribe/selectors';
import { customerDisplayName } from 'services/callScribe/participants';
import { ThunkGetCallScribeAccess } from 'store/callScribe/thunks';
import {
  protectTranscriptCall,
  releaseTranscriptCall,
  setActiveTranscriptCall,
} from 'store/callScribe';

import CallScribeAccessGate from 'components/CallScribe/CallScribeAccessGate';
import CallWindowErrorBoundary from 'components/CallScribe/Window/CallWindowErrorBoundary';
import CallScribeHome, { TDialLifecycleEvent } from 'components/CallScribe/Home/CallScribeHome';
import { CallStageView, ClosingFrame } from 'components/CallScribe/PopupCallView';
import PostCallReview from 'components/CallScribe/PostCallReview';
import CallEndedSteps from 'components/CallScribe/CallEndedSteps';
import CallIdentity from 'components/CallScribe/Dialer/CallIdentity';
import { useCallScribeTicketCalls } from 'components/CallScribe/useCallScribeTicketCalls';
import { useTwilioDevice } from 'components/CallScribe/useTwilioDevice';
import LiveCallBar from 'components/CallScribe/Window/LiveCallBar';
import WindowHeader from 'components/CallScribe/Window/WindowHeader';
import {
  formatPhoneE164ToDisplay,
  parseUtcInstant,
} from 'components/CallScribe/Recents/recentsHelpers';
import { dialFailureText } from 'components/CallScribe/Window/dialFailureText';
import { RESUME_BLOCKED_TEXT, resumeVerdict } from 'components/CallScribe/Window/resumeCall';
import { emitCallStateSignal } from 'components/CallScribe/callEndSignal';
import { useReportCallBusy } from 'components/CallScribe/callBusy';
import {
  acquireLiveCallLock,
  askLiveWindowToFocus,
  liveCallHeldMessage,
  readLiveCallHolder,
  releaseLiveCallLock,
  useLiveCallLease,
} from 'components/CallScribe/liveCallLease';
import {
  CLOSE_CONFIRM_CLOSE,
  CLOSE_CONFIRM_KEEP,
  CLOSE_CONFIRM_TITLE,
  closeConfirmBody,
  type TUnsavedWork,
} from 'components/CallScribe/closeConfirm';
import { getCallScribeCall } from 'services/callScribe';
import useWindowStyles from 'components/CallScribe/Window/windowStyles';
import {
  initialWindowScreenState,
  isCallInProgress,
  needsCloseConfirm,
  showsLiveCallBar,
  windowScreenReducer,
} from 'components/CallScribe/Window/windowScreen';

export const MAX_DIAL_ATTEMPTS = 3;
export const RETRY_READY_TIMEOUT_MS = 6000;
export const SOFTPHONE_UNAVAILABLE_TEXT =
  'The softphone is not connected, so calls cannot be placed yet.';
export const RETRY_NOT_READY_TEXT =
  'The softphone is still reconnecting. Try again in a moment.';
export const CRASH_TITLE = 'This window ran into a problem';
export const CRASH_HINT_IN_CALL =
  'Your call is still connected. You can mute or end it here.';
export const CRASH_HINT_IDLE =
  'The call history could not be shown. Nothing about your calls was lost.';

const CallScribeWindow: React.FC = () => {
  const classes = useWindowStyles();
  const dispatch = useAppDispatch();

  const [nav, navDispatch] = useReducer(windowScreenReducer, initialWindowScreenState);
  const [callId, setCallId] = useState<number | undefined>(undefined);
  const [dialTarget, setDialTarget] = useState('');
  const [dialContactName, setDialContactName] = useState<string | undefined>(undefined);
  const [dialNotice, setDialNotice] = useState<string | undefined>(undefined);
  const [attempts, setAttempts] = useState(0);
  const [failureText, setFailureText] = useState<string | undefined>(undefined);
  const [reviewUnsaved, setReviewUnsaved] = useState<TUnsavedWork | null>(null);
  const [askingToClose, setAskingToClose] = useState(false);
  const [retryPending, setRetryPending] = useState(false);
  const [resumeBlocked, setResumeBlocked] = useState(false);
  const dialledWhoRef = useRef('');
  const redialRef = useRef<
    ((to: string, contactExternalId?: string, contactName?: string) => void) | undefined
  >(undefined);

  useEffect(() => {
    dispatch(setConfigPsa(Integrations.Autotask));
    void dispatch(ThunkGetCallScribeAccess());
  }, [dispatch]);

  const config = useAppSelector(ConfigurationSelector);

  const twilio = useTwilioDevice();
  useSignalRCallScribeEvents();
  const { calls, loading, error: callsError, refetch } = useCallScribeTicketCalls();
  const answeredAtUtc = useAppSelector(CallScribeAnsweredAtForCallSelector(callId));
  const answeredAtMs = useMemo(() => {
    const parsed = parseUtcInstant(answeredAtUtc);
    return parsed ? parsed.getTime() : undefined;
  }, [answeredAtUtc]);

  const [contactName, setContactName] = useState('');

  const onDialLifecycle = useCallback((event: TDialLifecycleEvent) => {
    setDialTarget(event.to);
    setDialContactName(event.type === 'started' ? event.contactName : undefined);
    setDialNotice(event.type === 'started' ? event.documentationNotice : undefined);
    if (event.type === 'started') {
      setResumeBlocked(false);
      setCallId(event.callId);
      setFailureText(undefined);
      setAttempts(n => n + 1);
      navDispatch({ type: 'dialStarted' });
      return;
    }
    setCallId(event.callId);
    setFailureText(dialFailureText({ code: event.code, message: event.message }));
    navDispatch({ type: 'dialFailed' });
  }, []);

  useEffect(() => {
    if (typeof callId !== 'number') return;
    dispatch(setActiveTranscriptCall(callId));
  }, [callId, dispatch]);

  useEffect(() => {
    if (typeof callId !== 'number' || !isCallInProgress(nav)) return undefined;
    dispatch(protectTranscriptCall(callId));
    return () => { dispatch(releaseTranscriptCall()); };
  }, [callId, nav, dispatch]);

  const hadCallRef = useRef(false);

  useEffect(() => {
    if (twilio.state === 'in-call') {
      navDispatch({ type: 'connected' });
    }
  }, [twilio.state]);

  useEffect(() => {
    const hasCall = twilio.activeCall !== undefined;
    const had = hadCallRef.current;
    hadCallRef.current = hasCall;

    if (hasCall || !had) {
      return;
    }

    if (twilio.errorMessage !== undefined) {
      setFailureText(dialFailureText({
        code: twilio.errorCode,
        message: twilio.errorMessage,
      }));
      navDispatch({ type: 'dialFailed' });
      return;
    }
    navDispatch({ type: 'ended', hasCallId: typeof callId === 'number' });
  }, [twilio.activeCall, twilio.errorMessage, twilio.errorCode, callId]);

  const reportedErrorRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (twilio.state !== 'error') {
      reportedErrorRef.current = undefined;
      return;
    }
    if (!isCallInProgress(nav)) return;

    const key = `${twilio.errorCode ?? ''}|${twilio.errorMessage ?? ''}`;
    if (reportedErrorRef.current === key) return;
    reportedErrorRef.current = key;

    setFailureText(dialFailureText({
      code: twilio.errorCode,
      message: twilio.errorMessage,
    }));
    navDispatch({ type: 'dialFailed' });
  }, [twilio.state, twilio.errorCode, twilio.errorMessage, nav]);

  const retry = useCallback(() => {
    if (typeof callId !== 'number' || dialTarget.length === 0) return;
    void (async () => {
      const res = await getCallScribeCall(callId).catch(() => undefined);
      if (resumeVerdict(res?.data) !== 'resumable') {
        setResumeBlocked(true);
        setFailureText(RESUME_BLOCKED_TEXT);
        return;
      }
      if (!(await acquireLiveCallLock({ callId, callee: dialledWhoRef.current, toNumber: dialTarget }))) {
        setFailureText(`${liveCallHeldMessage(readLiveCallHolder())} End that call first.`);
        askLiveWindowToFocus();
        return;
      }
      twilio.recover();
      setRetryPending(true);
    })();
  }, [callId, dialTarget, twilio]);

  const callAgain = useCallback(() => {
    const dial = redialRef.current;
    if (dial === undefined || dialTarget.length === 0) return;
    void (async () => {
      if (!(await acquireLiveCallLock({ callId: null, callee: dialledWhoRef.current, toNumber: dialTarget }))) {
        setFailureText(`${liveCallHeldMessage(readLiveCallHolder())} End that call first.`);
        askLiveWindowToFocus();
        return;
      }
      setResumeBlocked(false);
      setFailureText(undefined);
      setCallId(undefined);
      setAttempts(0);
      dial(dialTarget, undefined, dialContactName);
    })();
  }, [dialTarget, dialContactName]);

  useEffect(() => {
    if (!retryPending) return undefined;

    if (twilio.state === 'ready') {
      setRetryPending(false);
      if (typeof callId === 'number' && dialTarget.length > 0) {
        setFailureText(undefined);
        setAttempts(n => n + 1);
        navDispatch({ type: 'dialStarted' });
        void twilio.placeCall({ to: dialTarget, callId });
      }
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setRetryPending(false);
      setFailureText(RETRY_NOT_READY_TEXT);
      navDispatch({ type: 'dialFailed' });
    }, RETRY_READY_TIMEOUT_MS);
    return () => { window.clearTimeout(timer); };
  }, [retryPending, twilio.state, twilio, callId, dialTarget]);

  const endedNow = useCallback(() => {
    navDispatch({ type: 'ended', hasCallId: typeof callId === 'number' });
  }, [callId]);

  const dismissReview = useCallback(() => {
    setReviewUnsaved(null);
    setCallId(undefined);
    setAttempts(0);
    navDispatch({ type: 'reviewDismissed' });
    refetch();
  }, [refetch]);

  const mustConfirmClose = needsCloseConfirm(nav) || reviewUnsaved !== null;

  const closingRef = useRef(false);

  const closeNow = useCallback(() => {
    closingRef.current = true;
    twilio.hangup();
    window.close();
  }, [twilio]);

  const requestClose = useCallback(() => {
    if (mustConfirmClose) {
      setAskingToClose(true);
      return;
    }
    window.close();
  }, [mustConfirmClose]);

  useEffect(() => {
    if (!isCallInProgress(nav) && reviewUnsaved === null) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent): void => {
      if (closingRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => { window.removeEventListener('beforeunload', onBeforeUnload); };
  }, [nav, reviewUnsaved]);

  useReportCallBusy(nav.session !== 'idle');

  const announcedRef = useRef(false);

  useEffect(() => {
    const up = isCallInProgress(nav);
    if (up === announcedRef.current) return;
    announcedRef.current = up;
    emitCallStateSignal(up ? 'started' : 'ended', callId);
  }, [nav, callId]);

  useEffect(() => {
    if (!isCallInProgress(nav)) return undefined;
    const onPageHide = (): void => {
      if (closingRef.current) return;
      closingRef.current = true;
      twilio.hangup();
    };
    window.addEventListener('pagehide', onPageHide);
    return () => { window.removeEventListener('pagehide', onPageHide); };
  }, [nav, twilio]);

  const capReached = attempts >= MAX_DIAL_ATTEMPTS;

  const dialledWho = dialContactName !== undefined && dialContactName.trim().length > 0
    ? dialContactName.trim()
    : formatPhoneE164ToDisplay(dialTarget) || dialTarget;

  const headerTitle = nav.screen === 'call'
    ? (dialledWho.length > 0 ? dialledWho : 'CallScribe')
    : 'CallScribe';
  const headerSubtitle = nav.screen === 'call' ? undefined : contactName;

  const inCall = isCallInProgress(nav);
  dialledWhoRef.current = dialledWho;

  const participants = useAppSelector(CallScribeParticipantsForCallSelector(callId));
  useLiveCallLease(
    inCall && dialTarget.length > 0
      ? { callId: callId ?? null, callee: customerDisplayName(participants, dialledWho), toNumber: dialTarget }
      : null,
  );

  useEffect(() => {
    if (!inCall && !retryPending) releaseLiveCallLock();
  }, [inCall, retryPending]);

  const crashFallback = useCallback((recover: () => void): React.ReactNode => (
    <div className={classes.failureBlock} role="alert">
      <span className={classes.crashTitle}>{CRASH_TITLE}</span>
      <span className={classes.failureHint}>
        {inCall ? CRASH_HINT_IN_CALL : CRASH_HINT_IDLE}
      </span>
      {inCall ? (
        <div className={classes.crashControls}>
          <Button variant="outlined" onClick={() => { twilio.mute(!twilio.isMuted); }}>
            {twilio.isMuted ? 'Unmute' : 'Mute'}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => { twilio.hangup(); endedNow(); }}
          >
            End call
          </Button>
        </div>
      ) : null}
      <Button onClick={recover}>Try again</Button>
    </div>
  ), [classes, inCall, twilio, endedNow]);

  return (
    <div className={classes.window}>
      <WindowHeader
        variant={nav.screen === 'call' ? 'call' : 'ticket'}
        title={headerTitle}
        subtitle={headerSubtitle}
        onGround={nav.screen === 'call'}
        onBack={() => { navDispatch({ type: 'back' }); }}
        onClose={requestClose}
      />

      <CallWindowErrorBoundary fallback={crashFallback}>
        <CallScribeAccessGate>
          {twilio.state === 'error' && !isCallInProgress(nav) && nav.screen === 'ticket' ? (
            <div className={classes.softphoneBanner} role="status">
              <span>{SOFTPHONE_UNAVAILABLE_TEXT}</span>
              <Button size="small" onClick={() => { twilio.recover(); }}>Try again</Button>
            </div>
          ) : null}

          {showsLiveCallBar(nav) ? (
            <LiveCallBar
              contactName={dialContactName}
              toNumber={dialTarget}
              live={nav.session === 'live'}
              answeredAtMs={answeredAtMs}
              isMuted={twilio.isMuted}
              onToggleMute={() => { twilio.mute(!twilio.isMuted); }}
              onEnd={() => { twilio.hangup(); endedNow(); }}
              onBackToCall={() => { navDispatch({ type: 'showCall' }); }}
            />
          ) : null}

          <div className={classes.screens}>
            <div className={classes.screen} hidden={nav.screen !== 'ticket'}>
              <CallScribeHome
                calls={calls}
                loading={loading}
                error={callsError}
                onRetry={refetch}
                twilio={twilio}
                onDialLifecycle={onDialLifecycle}
                onDialReady={dial => { redialRef.current = dial; }}
                onTicketContact={setContactName}
              />
            </div>

            <div className={classes.callScreen} hidden={nav.screen !== 'call'}>
              {nav.session === 'review' && typeof callId === 'number' ? (
                <CallEndedSteps
                  callId={callId}
                  who={dialledWho}
                  closeLabel="Back to ticket"
                  onClose={dismissReview}
                >
                  <PostCallReview
                    callId={callId}
                    crmSourceId={config.psaId ?? null}
                    documentationNotice={dialNotice}
                    onDismiss={dismissReview}
                    onUnsavedChange={setReviewUnsaved}
                  />
                </CallEndedSteps>
              ) : failureText !== undefined ? (
                <div className={`${classes.failureBlock} ${classes.onStage}`} role="alert">
                  <CallIdentity
                    name={dialledWho}
                    subtitle={formatPhoneE164ToDisplay(dialTarget) || dialTarget}
                    status="Call failed"
                    tone="error"
                    inputVolume={0}
                    outputVolume={0}
                    ringActive={false}
                  />
                  <p className={classes.failureCause}>{failureText}</p>
                  {resumeBlocked ? (
                    <Button variant="contained" onClick={callAgain}>Call again</Button>
                  ) : capReached ? (
                    <span className={classes.failureHint}>
                      That has failed {MAX_DIAL_ATTEMPTS} times. Close this window and open
                      CallScribe again.
                    </span>
                  ) : typeof callId === 'number' ? (
                    <Button variant="contained" onClick={retry} disabled={retryPending}>
                      {retryPending ? 'Reconnecting…' : 'Try again'}
                    </Button>
                  ) : (
                    <span className={classes.failureHint}>
                      Go back to the ticket and place the call again.
                    </span>
                  )}
                  <Button onClick={() => { navDispatch({ type: 'back' }); }}>Back to ticket</Button>
                </div>
              ) : nav.session === 'idle' ? (
                <div className={`${classes.failureBlock} ${classes.onStage}`}>
                  <ClosingFrame
                    tone="done"
                    title="Call ended"
                    hint="This call was not recorded against a ticket, so there is nothing to review."
                    hideClose
                  />
                  <Button onClick={() => { navDispatch({ type: 'back' }); }}>Back to ticket</Button>
                </div>
              ) : (
                <CallStageView
                  twilio={twilio}
                  target={dialTarget}
                  startError={undefined}
                  callId={callId}
                />
              )}
            </div>
          </div>
        </CallScribeAccessGate>
      </CallWindowErrorBoundary>

      <Dialog open={askingToClose} onClose={() => { setAskingToClose(false); }}>
        <DialogTitle>{inCall ? 'End call and close?' : CLOSE_CONFIRM_TITLE}</DialogTitle>
        <DialogContent>
          {inCall
            ? 'This will hang up the call in progress.'
            : closeConfirmBody(reviewUnsaved ?? 'notes')}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAskingToClose(false); }}>
            {inCall ? 'Keep talking' : CLOSE_CONFIRM_KEEP}
          </Button>
          <Button variant="contained" onClick={closeNow}>
            {inCall ? 'End and close' : CLOSE_CONFIRM_CLOSE}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CallScribeWindow;
